/**
 * FMA App entry: mounts React, sets up React Query and Supabase client provider.
 */
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { ThemeProvider } from "@/global/components/ui/theme-provider";
import { AuthProvider } from "@/global/auth/AuthContext";
import { ConfigurationProvider } from "@/global/ConfigurationContext";
import { PostHogProvider, PageViewTracker } from "@/global/lib/analytics";
import { TourProvider } from "@/global/contexts/TourContext";
import { JoyrideProvider } from "@/global/components/tour/TourProvider";
import { FMAApp } from "./root/FMAApp";
import "./index.css";

const qc = new QueryClient();

// Error fallback component for production error handling
function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a1628] text-white">
      <div className="text-center px-4">
        <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
        <p className="text-gray-400 mb-6">We're sorry for the inconvenience. Please refresh the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
        >
          Refresh Page
        </button>
        {process.env.NODE_ENV === "development" && (
          <details className="mt-8 text-left max-w-2xl mx-auto">
            <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-300">
              Error Details (dev only)
            </summary>
            <pre className="mt-4 p-4 bg-gray-900 rounded text-xs overflow-auto">
              {error.message}
              {"\n\n"}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

const el = document.getElementById("root")!;
createRoot(el).render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <BrowserRouter basename="/">
        <QueryClientProvider client={qc}>
          <ConfigurationProvider>
            <ThemeProvider defaultTheme="dark" storageKey="fma-theme">
              <AuthProvider>
                <TourProvider>
                  <JoyrideProvider>
                    <PostHogProvider>
                      <PageViewTracker appType="FMA" />
                      <FMAApp />
                    </PostHogProvider>
                  </JoyrideProvider>
                </TourProvider>
              </AuthProvider>
            </ThemeProvider>
          </ConfigurationProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
